import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Delete as DeleteIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  GridOn as GridIcon,
  CenterFocusStrong as CenterIcon,
  OpenWith as OpenWithIcon,
  Settings as SettingsIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  
  // Drawing Tools
  NearMe as SelectIcon,
  LocalParking as ParkingIcon,
  CropSquare as RectangleIcon,
  Circle as CircleIcon,
  ChangeHistory as ChangeHistoryIcon,
  
  // Layout Elements
  Business as BuildingIcon,
  Block as ObstacleIcon,
  Route as RoadIcon,
  ExitToApp as ExitIcon,
  Landscape as TreeIcon,
  TextFields as TextIcon,
  
  // Rotation
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIndicatorIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Layers as LayersIcon,
  Image as ImageIcon,
  
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

// Types
interface ParkingSpot {
  id: string;
  // Visual properties
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  color: string;
  borderColor?: string;
  borderWidth?: number;
  text?: string;
  fontSize?: number;
  textColor?: string;
  // Assignment properties (optional - assigned later)
  assigned_spot_id?: string; // ID of the real parking spot from database
  spot_number?: string;
  location?: string;
  spot_type?: 'regular' | 'maintenance' | 'washing' | 'premium';
  is_occupied?: boolean;
  layerZIndex?: number;
  layerHidden?: boolean;
  layerLocked?: boolean;
}

interface LayoutElement {
  id: string;
  type: 'rectangle' | 'circle' | 'triangle';
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  color: string;
  borderColor?: string;
  borderWidth?: number;
  text?: string;
  fontSize?: number;
  textColor?: string;
  layerZIndex?: number;
  layerHidden?: boolean;
  layerLocked?: boolean;
}

interface ParkingLayout {
  id?: string;
  name: string;
  description: string;
  width: number;
  height: number;
  spots: ParkingSpot[];
  elements: LayoutElement[];
  canvas?: {
    width?: number;
    height?: number;
    background_color: string;
    background_image?: string;
    /** Percent of natural size shown (25–300), stored also in layer_settings on save */
    background_image_scale?: number;
  };
  settings?: any;
  grid_settings?: any;
  snap_settings?: any;
  layer_settings?: any;
  created_at?: string;
  updated_at?: string;
}

type DrawingTool = 'select' | 'parking' | 'rectangle' | 'circle' | 'triangle';

// Constants
const CANVAS_WIDTH = 1400;
const CANVAS_HEIGHT = 900;
const GRID_SIZE = 20;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

const SPOT_TYPES = {
  regular: { label: 'Standard', color: '#2196F3' },
  premium: { label: 'Premium', color: '#4CAF50' },
  maintenance: { label: 'Maintenance', color: '#FF9800' },
  washing: { label: 'Lavage', color: '#9C27B0' },
};

const ELEMENT_TYPES = {
  rectangle: { label: 'Rectangle', color: '#2196F3', icon: RectangleIcon, defaultSize: { width: 100, height: 60 } },
  circle: { label: 'Cercle', color: '#2196F3', icon: CircleIcon, defaultSize: { width: 80, height: 80 } },
  triangle: { label: 'Triangle', color: '#2196F3', icon: ChangeHistoryIcon, defaultSize: { width: 80, height: 80 } },
};

const getNextSpotNumber = (existingSpotNumbers: string[], preferredPrefix = 'P'): string => {
  const parsedSpots = existingSpotNumbers
    .map((value) => value?.trim())
    .filter(Boolean)
    .map((spot) => {
      const match = /^([A-Za-z]+)?(\d+)$/.exec(spot as string);
      if (!match) return null;
      return {
        prefix: (match[1] || preferredPrefix).toUpperCase(),
        number: parseInt(match[2], 10),
      };
    })
    .filter((parsed): parsed is { prefix: string; number: number } => parsed !== null);

  const preferred = preferredPrefix.toUpperCase();
  const samePrefix = parsedSpots.filter((spot) => spot.prefix === preferred);
  const candidatePool = samePrefix.length > 0 ? samePrefix : parsedSpots;
  const maxNumber = candidatePool.reduce((max, spot) => Math.max(max, spot.number), 0);

  return `${preferred}${String(maxNumber + 1).padStart(2, '0')}`;
};

type LayerKind = 'element' | 'spot';

interface LayerRow {
  kind: LayerKind;
  id: string;
  label: string;
  layerZIndex: number;
  layerHidden: boolean;
  layerLocked: boolean;
}

function getApiOrigin(): string {
  const raw = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return raw.replace(/\/?api\/?$/i, '').replace(/\/$/, '');
}

function resolveBackgroundImageUrl(backgroundImage?: string | null): string | null {
  if (!backgroundImage?.trim()) return null;
  if (backgroundImage.startsWith('http://') || backgroundImage.startsWith('https://') || backgroundImage.startsWith('data:')) {
    return backgroundImage;
  }
  return `${getApiOrigin()}/${backgroundImage.replace(/^\//, '')}`;
}

function computeNextLayerZ(layout: ParkingLayout): number {
  const nums = [
    ...layout.elements.map((e) => (typeof e.layerZIndex === 'number' ? e.layerZIndex : 0)),
    ...layout.spots.map((s) => (typeof s.layerZIndex === 'number' ? s.layerZIndex : 0)),
  ];
  return (nums.length ? Math.max(...nums) : 0) + 1;
}

/** Ensure every spot/element has layer fields; merge background scale into layer_settings */
function normalizeLayerFields(layout: ParkingLayout): ParkingLayout {
  let next =
    Math.max(
      0,
      ...layout.elements.map((e) => (typeof e.layerZIndex === 'number' ? e.layerZIndex : 0)),
      ...layout.spots.map((s) => (typeof s.layerZIndex === 'number' ? s.layerZIndex : 0)),
    ) + 1;

  const elements = layout.elements.map((e) => ({
    ...e,
    layerZIndex: typeof e.layerZIndex === 'number' ? e.layerZIndex : next++,
    layerHidden: e.layerHidden ?? false,
    layerLocked: e.layerLocked ?? false,
  }));

  const spots = layout.spots.map((s) => ({
    ...s,
    layerZIndex: typeof s.layerZIndex === 'number' ? s.layerZIndex : next++,
    layerHidden: s.layerHidden ?? false,
    layerLocked: s.layerLocked ?? false,
  }));

  const ls = { ...(layout.layer_settings || {}) };
  const scaleRaw = ls.background_image_scale ?? layout.canvas?.background_image_scale;
  const scale =
    typeof scaleRaw === 'number' && Number.isFinite(scaleRaw) ? Math.min(300, Math.max(5, scaleRaw)) : 100;

  const canvas = {
    ...layout.canvas,
    background_color: layout.canvas?.background_color || '#ffffff',
    background_image_scale: scale,
  };

  return {
    ...layout,
    elements,
    spots,
    canvas,
    layer_settings: { ...ls, background_image_scale: scale },
  };
}

function isLayerLocked(item: ParkingSpot | LayoutElement | null): boolean {
  return !!(item && item.layerLocked);
}

function buildLayerRows(layout: ParkingLayout): LayerRow[] {
  const rows: LayerRow[] = [
    ...layout.elements.map((el) => ({
      kind: 'element' as const,
      id: el.id,
      label: el.name || `${el.type}`,
      layerZIndex: el.layerZIndex ?? 1,
      layerHidden: el.layerHidden ?? false,
      layerLocked: el.layerLocked ?? false,
    })),
    ...layout.spots.map((sp) => ({
      kind: 'spot' as const,
      id: sp.id,
      label: sp.spot_number?.trim() || sp.text?.trim() || 'Place',
      layerZIndex: sp.layerZIndex ?? 1,
      layerHidden: sp.layerHidden ?? false,
      layerLocked: sp.layerLocked ?? false,
    })),
  ];
  rows.sort((a, b) => b.layerZIndex - a.layerZIndex);
  return rows;
}

const ParkingEditor: React.FC = () => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragStartElementPos, setDragStartElementPos] = useState<{ x: number; y: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentMousePos, setCurrentMousePos] = useState<{ x: number; y: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationStart, setRotationStart] = useState<{ x: number; y: number; angle: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Core State
  const [layout, setLayout] = useState<ParkingLayout>(() =>
    normalizeLayerFields({
      name: 'Nouveau Plan',
      description: '',
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      spots: [],
      elements: [],
      canvas: {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        background_color: '#ffffff',
        background_image_scale: 100,
      },
      layer_settings: { background_image_scale: 100 },
    })
  );

  // UI State
  const [selectedTool, setSelectedTool] = useState<DrawingTool>('select');
  const [selectedElement, setSelectedElement] = useState<ParkingSpot | LayoutElement | null>(null);
  const [selectedColor, setSelectedColor] = useState('#2196f3'); // Default blue
  // Properties panel is now always visible
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(true);
  const [panZoomMode, setPanZoomMode] = useState(false);
  // History for undo/redo
  const [history, setHistory] = useState<ParkingLayout[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Dialogs
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showPlanSizeDialog, setShowPlanSizeDialog] = useState(false);
  const [showLayoutSelectionDialog, setShowLayoutSelectionDialog] = useState(false);
  const [isCreatingNewPlan, setIsCreatingNewPlan] = useState(false);
  
  // Plan size form state
  const [planSizeForm, setPlanSizeForm] = useState({ width: 1400, height: 900 });
  
  // Debug logging for dialog state
  useEffect(() => {
    console.log('showSaveDialog state changed:', showSaveDialog);
  }, [showSaveDialog]);

  // Sync plan size form with current layout when dialog opens
  useEffect(() => {
    if (showPlanSizeDialog) {
      setPlanSizeForm({ width: layout.width, height: layout.height });
    }
  }, [showPlanSizeDialog, layout.width, layout.height]);

  // Loading states
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState<ParkingSpot | LayoutElement | null>(null);
  
  // Available parking spots for assignment
  const [availableParkingSpots, setAvailableParkingSpots] = useState<any[]>([]);
  const [showSpotAssignmentDialog, setShowSpotAssignmentDialog] = useState(false);
  const [editingSpotDetails, setEditingSpotDetails] = useState(false);
  const [spotDetailsLoading, setSpotDetailsLoading] = useState(false);
  
  // Parking layouts management
  const [availableLayouts, setAvailableLayouts] = useState<any[]>([]);
  const [currentLayoutId, setCurrentLayoutId] = useState<string | null>(null);
  const [isCurrentPlan, setIsCurrentPlan] = useState(false);
  const backgroundFileInputRef = useRef<HTMLInputElement>(null);
  const [layerDragIndex, setLayerDragIndex] = useState<number | null>(null);

  const resolvedBackgroundUrl = useMemo(
    () => resolveBackgroundImageUrl(layout.canvas?.background_image),
    [layout.canvas?.background_image]
  );

  const backgroundScalePercent =
    typeof layout.layer_settings?.background_image_scale === 'number'
      ? layout.layer_settings.background_image_scale
      : typeof layout.canvas?.background_image_scale === 'number'
        ? layout.canvas!.background_image_scale!
        : 100;

  const layerRows = useMemo(() => buildLayerRows(layout), [layout.elements, layout.spots]);

  /** Bottom → top stacking (ascending layerZIndex renders first so higher z stacks on top) */
  const sortedRenderable = useMemo(() => {
    const items: (
      | { kind: 'element'; item: LayoutElement }
      | { kind: 'spot'; item: ParkingSpot }
    )[] = [
      ...layout.elements.map((item) => ({ kind: 'element' as const, item })),
      ...layout.spots.map((item) => ({ kind: 'spot' as const, item })),
    ];
    items.sort((a, b) => (a.item.layerZIndex ?? 1) - (b.item.layerZIndex ?? 1));
    return items;
  }, [layout.elements, layout.spots]);
  // Load available parking spots
  const loadAvailableParkingSpots = useCallback(async () => {
    try {
      const response = await api.get('/parking-spots');
      const spots = response.data.data || response.data || [];
      setAvailableParkingSpots(spots);
    } catch (err) {
      console.error('Error loading parking spots:', err);
      setAvailableParkingSpots([]);
    }
  }, []);

  // Load available parking layouts
  const loadAvailableLayouts = useCallback(async (preserveCurrentSelection = false) => {
    try {
      console.log('Loading parking layouts...');
      const response = await api.get('/parking-layouts');
      console.log('Layouts response:', response.data);
      const layouts = response.data.data || response.data || [];
      console.log('Available layouts:', layouts);
      console.log('Active layouts:', layouts.filter((l: any) => l.is_active));
      setAvailableLayouts(layouts);
      
      // Only set current layout if we don't have one selected AND we're not preserving current selection AND we're not creating a new plan
      if (!currentLayoutId && !preserveCurrentSelection && !isCreatingNewPlan) {
        // Find current layout (is_active = true)
        const currentLayout = layouts.find((layout: any) => layout.is_active);
        if (currentLayout) {
          console.log('Found current layout:', currentLayout);
          setCurrentLayoutId(currentLayout.id.toString());
          setIsCurrentPlan(true);
        } else {
          console.log('No current layout found');
        }
      } else if (currentLayoutId) {
        // Update isCurrentPlan status for the currently selected layout
        const selectedLayout = layouts.find((layout: any) => layout.id.toString() === currentLayoutId);
        if (selectedLayout) {
          setIsCurrentPlan(selectedLayout.is_active || false);
        }
      }
    } catch (err) {
      console.error('Error loading parking layouts:', err);
      setAvailableLayouts([]);
    }
  }, [currentLayoutId, isCreatingNewPlan]);

  // Load specific layout data
  const loadLayoutData = useCallback(async (layoutId: string) => {
    try {
      const response = await api.get(`/parking-layouts/${layoutId}`);
      const layoutData = response.data.data || response.data;
      
      // Update layout state with loaded data
      console.log('Loading layout data:', layoutData);
      const newLayout = {
        id: layoutData.id,
        name: layoutData.name,
        description: layoutData.description || '',
        width: layoutData.canvas?.width || CANVAS_WIDTH,
        height: layoutData.canvas?.height || CANVAS_HEIGHT,
        spots: layoutData.settings?.visual_spots || layoutData.spots || [],
        elements: layoutData.settings?.visual_elements || layoutData.elements || [],
        canvas: layoutData.canvas || { width: CANVAS_WIDTH, height: CANVAS_HEIGHT, background_color: '#ffffff', background_image_scale: 100 },
        settings: layoutData.settings || {},
        grid_settings: layoutData.grid_settings || { enabled: true, size: 20 },
        snap_settings: layoutData.snap_settings || { enabled: true, threshold: 10 },
        layer_settings: layoutData.layer_settings || {},
      };
      console.log('Setting layout to:', newLayout);
      setLayout(normalizeLayerFields(newLayout));
      
      setCurrentLayoutId(layoutId);
      setIsCurrentPlan(layoutData.is_active || false);
    } catch (err) {
      console.error('Error loading layout data:', err);
      setError('Erreur lors du chargement du plan');
    }
  }, [setLayout, setError]);

  // Handle layout selection
  const handleLayoutSelect = useCallback((layoutId: string) => {
    if (layoutId && layoutId !== currentLayoutId) {
      loadLayoutData(layoutId);
      setShowLayoutSelectionDialog(false);
    }
  }, [currentLayoutId, loadLayoutData]);

  // Toggle layout active status
  const handleToggleActive = useCallback(async (layoutId: string) => {
    try {
      console.log('Toggling layout active status:', layoutId, 'Currently selected:', currentLayoutId);
      
      // Find the current layout to check its active status
      const layout = availableLayouts.find(l => l.id.toString() === layoutId);
      const isCurrentlyActive = layout?.is_active || false;
      
      if (isCurrentlyActive) {
        // Deactivate the layout
        console.log('Deactivating layout:', layoutId);
        const deactivateResponse = await api.patch(`/parking-layouts/${layoutId}/deactivate`);
        console.log('Deactivate response:', deactivateResponse.data);
        setSuccess('Plan désactivé');
      } else {
        // Activate the layout
        console.log('Activating layout:', layoutId);
        const activateResponse = await api.patch(`/parking-layouts/${layoutId}/activate`);
        console.log('Activate response:', activateResponse.data);
        setSuccess('Plan activé');
      }
      
      // Update local state only if the currently selected layout is the one being toggled
      if (currentLayoutId === layoutId) {
        setIsCurrentPlan(!isCurrentlyActive);
      }
      
      // Reload layouts to update the active status
      await loadAvailableLayouts(true);
      
    } catch (err) {
      console.error('Error toggling layout active status:', err);
      setError('Erreur lors de la modification du statut du plan');
    }
  }, [currentLayoutId, availableLayouts, loadAvailableLayouts, setSuccess, setError]);


  // Create new plan
  const createNewPlan = useCallback(() => {
    const newLayout = normalizeLayerFields({
      name: 'Nouveau Plan',
      description: '',
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      spots: [],
      elements: [],
      canvas: {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        background_color: '#ffffff',
        background_image_scale: 100,
      },
      layer_settings: { background_image_scale: 100 },
    });
    
    setLayout(newLayout);
    setCurrentLayoutId(null);
    setIsCurrentPlan(false);
    setHistory([newLayout]);
    setHistoryIndex(0);
    setSelectedElement(null);
  }, [setLayout, setCurrentLayoutId, setIsCurrentPlan, setHistory, setHistoryIndex, setSelectedElement]);

  // Initialize
  useEffect(() => {
    const initializeEditor = async () => {
      // Load available layouts first
      await loadAvailableLayouts();
      
      // Load parking spots
      await loadAvailableParkingSpots();
    };
    
    initializeEditor();
  }, [loadAvailableLayouts, loadAvailableParkingSpots]);

  // Load layout data when currentLayoutId is set
  useEffect(() => {
    if (currentLayoutId && !isCreatingNewPlan) {
      loadLayoutData(currentLayoutId);
    }
  }, [currentLayoutId, loadLayoutData, isCreatingNewPlan]);

  // History Management
  const addToHistory = useCallback((newLayout: ParkingLayout) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newLayout)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setLayout(JSON.parse(JSON.stringify(history[newIndex])));
      setSelectedElement(null);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setLayout(JSON.parse(JSON.stringify(history[newIndex])));
      setSelectedElement(null);
    }
  }, [history, historyIndex]);

  // Canvas Interactions
  const getCanvasCoordinates = useCallback((clientX: number, clientY: number) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panOffset.x) / zoom,
      y: (clientY - rect.top - panOffset.y) / zoom,
    };
  }, [zoom, panOffset]);

  const findElementAtPosition = useCallback((x: number, y: number) => {
    const visibleElements = layout.elements.filter((e) => !e.layerHidden);
    const visibleSpots = layout.spots.filter((s) => !s.layerHidden);
    const merged: (ParkingSpot | LayoutElement)[] = [...visibleElements, ...visibleSpots].sort(
      (a, b) => (b.layerZIndex ?? 1) - (a.layerZIndex ?? 1)
    );
    for (const item of merged) {
      if (x >= item.x && x <= item.x + item.width && y >= item.y && y <= item.y + item.height) {
        return item;
      }
    }

    return null;
  }, [layout]);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click

    // If in pan/zoom mode, start panning
    if (panZoomMode) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      return;
    }

    const coords = getCanvasCoordinates(e.clientX, e.clientY);
    setDragStart(coords);

    if (selectedTool === 'select') {
      // Check if clicking on an element (top-most visible first)
      const clickedElement = findElementAtPosition(coords.x, coords.y);
      setSelectedElement(clickedElement);
      if (clickedElement && !isLayerLocked(clickedElement)) {
        setIsDragging(true);
        // Store the element's original position when drag starts
        setDragStartElementPos({ x: clickedElement.x, y: clickedElement.y });
      }
    } else {
      // Deselect when drawing new elements
      setSelectedElement(null);
      setIsDrawing(true);
    }
  }, [selectedTool, getCanvasCoordinates, layout, panZoomMode, panOffset, findElementAtPosition]);

  // Calculate rotation angle from mouse position
  const calculateRotationAngle = useCallback((mouseX: number, mouseY: number, element: ParkingSpot | LayoutElement) => {
    const centerX = element.x + element.width / 2;
    const centerY = element.y + element.height / 2;
    const angle = Math.atan2(mouseY - centerY, mouseX - centerX) * (180 / Math.PI);
    return angle;
  }, []);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    const coords = getCanvasCoordinates(e.clientX, e.clientY);
    setCurrentMousePos(coords);
    
    // Handle panning
    if (isPanning) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }
    
    if (!isDragging && !isDrawing && !isResizing && !isRotating) return;

    if (isDragging && selectedElement && dragStart && dragStartElementPos) {
      const deltaX = coords.x - dragStart.x;
      const deltaY = coords.y - dragStart.y;
      
      // Calculate new position based on original position + delta
      const newX = Math.max(0, Math.min(layout.width - selectedElement.width, dragStartElementPos.x + deltaX));
      const newY = Math.max(0, Math.min(layout.height - selectedElement.height, dragStartElementPos.y + deltaY));
      
      const updatedLayout = { ...layout };
      
      if (layout.spots.some(spot => spot.id === selectedElement.id)) {
        // Moving parking spot
        const spotIndex = updatedLayout.spots.findIndex(s => s.id === selectedElement.id);
        if (spotIndex !== -1) {
          const updatedSpot = {
            ...updatedLayout.spots[spotIndex],
            x: newX,
            y: newY,
          };
          updatedLayout.spots[spotIndex] = updatedSpot;
          setSelectedElement(updatedSpot); // Update selected element reference
        }
      } else {
        // Moving layout element
        const elementIndex = updatedLayout.elements.findIndex(e => e.id === selectedElement.id);
        if (elementIndex !== -1) {
          const updatedElement = {
            ...updatedLayout.elements[elementIndex],
            x: newX,
            y: newY,
          };
          updatedLayout.elements[elementIndex] = updatedElement;
          setSelectedElement(updatedElement); // Update selected element reference
        }
      }
      
      setLayout(updatedLayout);
    }

    // Handle resizing
    if (isResizing && selectedElement && resizeStart && resizeHandle) {
      const deltaX = coords.x - dragStart!.x;
      const deltaY = coords.y - dragStart!.y;
      
      let newX = resizeStart.x;
      let newY = resizeStart.y;
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      // Calculate new dimensions based on resize handle
      switch (resizeHandle) {
        case 'nw': // Top-left
          newX = resizeStart.x + deltaX;
          newY = resizeStart.y + deltaY;
          newWidth = resizeStart.width - deltaX;
          newHeight = resizeStart.height - deltaY;
          break;
        case 'ne': // Top-right
          newY = resizeStart.y + deltaY;
          newWidth = resizeStart.width + deltaX;
          newHeight = resizeStart.height - deltaY;
          break;
        case 'sw': // Bottom-left
          newX = resizeStart.x + deltaX;
          newWidth = resizeStart.width - deltaX;
          newHeight = resizeStart.height + deltaY;
          break;
        case 'se': // Bottom-right
          newWidth = resizeStart.width + deltaX;
          newHeight = resizeStart.height + deltaY;
          break;
        case 'n': // Top
          newY = resizeStart.y + deltaY;
          newHeight = resizeStart.height - deltaY;
          break;
        case 's': // Bottom
          newHeight = resizeStart.height + deltaY;
          break;
        case 'w': // Left
          newX = resizeStart.x + deltaX;
          newWidth = resizeStart.width - deltaX;
          break;
        case 'e': // Right
          newWidth = resizeStart.width + deltaX;
          break;
      }

      // Ensure minimum size
      newWidth = Math.max(20, newWidth);
      newHeight = Math.max(20, newHeight);

      // Ensure element stays within canvas bounds
      newX = Math.max(0, Math.min(layout.width - newWidth, newX));
      newY = Math.max(0, Math.min(layout.height - newHeight, newY));

      const updatedLayout = { ...layout };
      
      if (layout.spots.some(spot => spot.id === selectedElement.id)) {
        const spotIndex = updatedLayout.spots.findIndex(s => s.id === selectedElement.id);
        if (spotIndex !== -1) {
          const updatedSpot = {
            ...updatedLayout.spots[spotIndex],
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight,
          };
          updatedLayout.spots[spotIndex] = updatedSpot;
          setSelectedElement(updatedSpot);
        }
      } else {
        const elementIndex = updatedLayout.elements.findIndex(e => e.id === selectedElement.id);
        if (elementIndex !== -1) {
          const updatedElement = {
            ...updatedLayout.elements[elementIndex],
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight,
          };
          updatedLayout.elements[elementIndex] = updatedElement;
          setSelectedElement(updatedElement);
        }
      }
      
      setLayout(updatedLayout);
    }

    // Handle rotation
    if (isRotating && selectedElement && rotationStart) {
      const newAngle = calculateRotationAngle(coords.x, coords.y, selectedElement);
      const rotation = newAngle - rotationStart.angle;
      
      // Normalize rotation to 0-360 range
      const normalizedRotation = ((rotation % 360) + 360) % 360;
      
      const updatedLayout = { ...layout };
      
      if (layout.spots.some(spot => spot.id === selectedElement.id)) {
        const spotIndex = updatedLayout.spots.findIndex(s => s.id === selectedElement.id);
        if (spotIndex !== -1) {
          const updatedSpot = {
            ...updatedLayout.spots[spotIndex],
            rotation: normalizedRotation,
          };
          updatedLayout.spots[spotIndex] = updatedSpot;
          setSelectedElement(updatedSpot);
        }
      } else {
        const elementIndex = updatedLayout.elements.findIndex(e => e.id === selectedElement.id);
        if (elementIndex !== -1) {
          const updatedElement = {
            ...updatedLayout.elements[elementIndex],
            rotation: normalizedRotation,
          };
          updatedLayout.elements[elementIndex] = updatedElement;
          setSelectedElement(updatedElement);
        }
      }
      
      setLayout(updatedLayout);
    }
  }, [isDragging, isDrawing, isResizing, isRotating, isPanning, panStart, selectedElement, dragStart, dragStartElementPos, resizeStart, resizeHandle, rotationStart, layout, getCanvasCoordinates, calculateRotationAngle]);

  const handleCanvasMouseUp = useCallback((e: React.MouseEvent) => {
    const coords = getCanvasCoordinates(e.clientX, e.clientY);

    // Stop panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (isDrawing && dragStart && selectedTool !== 'select') {
      const width = Math.abs(coords.x - dragStart.x);
      const height = Math.abs(coords.y - dragStart.y);
      
      if (width > 10 && height > 10) {
        if (selectedTool === 'parking') {
          createParkingSpot(
            Math.min(dragStart.x, coords.x),
            Math.min(dragStart.y, coords.y),
            width,
            height
          );
        } else {
          createElement(
            selectedTool,
            Math.min(dragStart.x, coords.x),
            Math.min(dragStart.y, coords.y),
            width,
            height
          );
        }
      }
    }

    if (isDragging || isDrawing || isResizing || isRotating) {
      addToHistory(layout);
    }

    setIsDragging(false);
    setIsDrawing(false);
    setIsResizing(false);
    setIsRotating(false);
    setDragStart(null);
    setDragStartElementPos(null);
    setResizeHandle(null);
    setResizeStart(null);
    setRotationStart(null);
    setCurrentMousePos(null);
  }, [isDrawing, isDragging, isResizing, isRotating, dragStart, selectedTool, layout, getCanvasCoordinates, addToHistory]);

  // Handle resize handle clicks
  const handleResizeStart = useCallback((handle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedElement) return;
    if (isLayerLocked(selectedElement)) return;
    
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStart({
      x: selectedElement.x,
      y: selectedElement.y,
      width: selectedElement.width,
      height: selectedElement.height,
    });
    setDragStart(getCanvasCoordinates(e.clientX, e.clientY));
  }, [selectedElement, getCanvasCoordinates]);

  // Handle rotation start
  const handleRotationStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedElement) return;
    if (isLayerLocked(selectedElement)) return;
    
    setIsRotating(true);
    const coords = getCanvasCoordinates(e.clientX, e.clientY);
    const centerX = selectedElement.x + selectedElement.width / 2;
    const centerY = selectedElement.y + selectedElement.height / 2;
    const angle = Math.atan2(coords.y - centerY, coords.x - centerX) * (180 / Math.PI);
    
    setRotationStart({
      x: coords.x,
      y: coords.y,
      angle: angle - selectedElement.rotation,
    });
  }, [selectedElement, getCanvasCoordinates]);

  // Handle rotation by buttons
  const handleRotateLeft = useCallback(() => {
    if (!selectedElement) return;
    if (isLayerLocked(selectedElement)) return;

    const newRotation = (selectedElement.rotation - 15 + 360) % 360;
    const updatedLayout = { ...layout };
    
    if (layout.spots.some(spot => spot.id === selectedElement.id)) {
      const spotIndex = updatedLayout.spots.findIndex(s => s.id === selectedElement.id);
      if (spotIndex !== -1) {
        const updatedSpot = { ...updatedLayout.spots[spotIndex], rotation: newRotation };
        updatedLayout.spots[spotIndex] = updatedSpot;
        setSelectedElement(updatedSpot);
      }
    } else {
      const elementIndex = updatedLayout.elements.findIndex(e => e.id === selectedElement.id);
      if (elementIndex !== -1) {
        const updatedElement = { ...updatedLayout.elements[elementIndex], rotation: newRotation };
        updatedLayout.elements[elementIndex] = updatedElement;
        setSelectedElement(updatedElement);
      }
    }
    
    setLayout(updatedLayout);
    addToHistory(updatedLayout);
  }, [selectedElement, layout, addToHistory]);

  const handleRotateRight = useCallback(() => {
    if (!selectedElement) return;
    if (isLayerLocked(selectedElement)) return;

    const newRotation = (selectedElement.rotation + 15) % 360;
    const updatedLayout = { ...layout };
    
    if (layout.spots.some(spot => spot.id === selectedElement.id)) {
      const spotIndex = updatedLayout.spots.findIndex(s => s.id === selectedElement.id);
      if (spotIndex !== -1) {
        const updatedSpot = { ...updatedLayout.spots[spotIndex], rotation: newRotation };
        updatedLayout.spots[spotIndex] = updatedSpot;
        setSelectedElement(updatedSpot);
      }
    } else {
      const elementIndex = updatedLayout.elements.findIndex(e => e.id === selectedElement.id);
      if (elementIndex !== -1) {
        const updatedElement = { ...updatedLayout.elements[elementIndex], rotation: newRotation };
        updatedLayout.elements[elementIndex] = updatedElement;
        setSelectedElement(updatedElement);
      }
    }
    
    setLayout(updatedLayout);
    addToHistory(updatedLayout);
  }, [selectedElement, layout, addToHistory]);

  const createParkingSpot = useCallback(async (x: number, y: number, width: number, height: number) => {
    try {
      // Generate a unique spot number
      const spotNumber = `P${Date.now().toString().slice(-4)}`;
      
      // Create the real parking spot in the database
      const spotData = {
        spot_number: spotNumber,
        location: 'Plan de parking',
        spot_type: 'regular',
        is_occupied: false,
        // Visual editor fields
        x_position: x,
        y_position: y,
        width: Math.max(width, 80),
        height: Math.max(height, 40),
        rotation: 0,
        color: '#2196F3', // Blue for regular spots
        border_color: '#000000',
        border_width: 1,
        label: spotNumber,
        label_position: 'center',
        label_color: '#ffffff',
        label_size: 12,
        is_visible: true,
        z_index: 1,
        custom_properties: null,
        // Layout management
        layout_name: layout.name,
        layout_version: 1,
        is_template: false,
        template_category: null,
        // Enhanced spot information
        length_meters: null,
        width_meters: null,
        height_meters: null,
        max_weight_kg: null,
        notes: null,
        amenities: null,
        // Status and assignment
        assigned_vehicle_id: null,
        assigned_at: null,
        last_occupied_at: null,
        occupancy_count: 0,
      };

      const response = await api.post('/parking-spots', spotData);
      const createdSpot = response.data.data || response.data;

      setLayout((prev) => {
        const z = computeNextLayerZ(prev);
        const newSpot: ParkingSpot = {
          id: `visual_spot_${Date.now()}`,
          x,
          y,
          width: Math.max(width, 80),
          height: Math.max(height, 40),
          rotation: 0,
          color: '#2196F3',
          borderColor: '#000000',
          borderWidth: 1,
          text: spotNumber,
          fontSize: 12,
          textColor: '#ffffff',
          assigned_spot_id: createdSpot.id.toString(),
          spot_number: spotNumber,
          location: 'Plan de parking',
          spot_type: 'regular',
          is_occupied: false,
          layerZIndex: z,
          layerHidden: false,
          layerLocked: false,
        };
        return { ...prev, spots: [...prev.spots, newSpot] };
      });

      console.log('Created parking spot:', createdSpot);
    } catch (error) {
      console.error('Error creating parking spot:', error);
      // Fallback: create visual spot only
      setLayout((prev) => {
        const z = computeNextLayerZ(prev);
        const newSpot: ParkingSpot = {
          id: `visual_spot_${Date.now()}`,
          x,
          y,
          width: Math.max(width, 80),
          height: Math.max(height, 40),
          rotation: 0,
          color: '#e0e0e0',
          borderColor: '#000000',
          borderWidth: 0,
          text: 'Erreur création',
          fontSize: 12,
          textColor: '#666666',
          assigned_spot_id: undefined,
          spot_number: undefined,
          location: undefined,
          spot_type: undefined,
          is_occupied: false,
          layerZIndex: z,
          layerHidden: false,
          layerLocked: false,
        };
        return { ...prev, spots: [...prev.spots, newSpot] };
      });
    }
  }, [layout.name]);

  const createElement = useCallback((type: DrawingTool, x: number, y: number, width: number, height: number) => {
    if (type === 'select' || type === 'parking') return;

    const elementType = ELEMENT_TYPES[type as keyof typeof ELEMENT_TYPES];
    setLayout((prev) => {
      const z = computeNextLayerZ(prev);
      const newElement: LayoutElement = {
        id: `${type}_${Date.now()}`,
        type: type as any,
        name: elementType.label,
        x,
        y,
        width: Math.max(width, elementType.defaultSize.width / 2),
        height: Math.max(height, elementType.defaultSize.height / 2),
        rotation: 0,
        color: selectedColor,
        borderColor: '#000000',
        borderWidth: 0,
        text: undefined,
        fontSize: undefined,
        textColor: '#000000',
        layerZIndex: z,
        layerHidden: false,
        layerLocked: false,
      };

      return {
        ...prev,
        elements: [...prev.elements, newElement],
      };
    });
  }, [selectedColor]);

  // Zoom and Pan
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.2, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.2, MIN_ZOOM));
  }, []);

  const handleCenterView = useCallback(() => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Handle wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!panZoomMode) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * delta));
    setZoom(newZoom);
  }, [panZoomMode, zoom]);

  // Plan size confirmation
  const handleConfirmPlanSize = useCallback(() => {
    const { width, height } = planSizeForm;
    
    // Validate dimensions
    if (width < 400 || width > 4000 || height < 300 || height > 3000) {
      alert('Dimensions invalides. Largeur: 400-4000px, Hauteur: 300-3000px');
      return;
    }
    
    // Apply the new dimensions
    setLayout(prev => ({ ...prev, width, height }));
    setShowPlanSizeDialog(false);
  }, [planSizeForm]);

  // Save Layout (handles both creating new and updating existing)
  const handleSave = useCallback(async () => {
    console.log('Save button clicked, starting save process...');
    setSaving(true);
    
    try {
      // Save the layout to the parking-layouts endpoint
      const layoutData = {
        name: layout.name,
        description: layout.description,
        category: 'custom', // Required field
        canvas_width: layout.width,
        canvas_height: layout.height,
        background_color: layout.canvas?.background_color || '#ffffff',
        background_image: layout.canvas?.background_image || null,
        scale_factor: 1,
        scale_unit: 'meters',
        settings: {
          ...layout.settings,
          visual_spots: layout.spots,
          visual_elements: layout.elements
        },
        grid_settings: layout.grid_settings || { enabled: true, size: 20 },
        snap_settings: layout.snap_settings || { enabled: true, threshold: 10 },
        layer_settings: {
          ...(layout.layer_settings || {}),
          background_image_scale:
            layout.layer_settings?.background_image_scale ??
            layout.canvas?.background_image_scale ??
            100,
        },
        is_template: false,
        is_preset: false,
        is_active: currentLayoutId ? undefined : false, // New layouts should be inactive by default
      };

      console.log('Saving layout data:', layoutData);
      console.log('Current layout state:', { name: layout.name, currentLayoutId });

      let response;
      if (currentLayoutId) {
        // Update existing layout
        response = await api.put(`/parking-layouts/${currentLayoutId}`, layoutData);
        console.log('Layout updated:', response.data);
      } else {
        // Create new layout
        console.log('Creating new layout with data:', layoutData);
        response = await api.post('/parking-layouts', layoutData);
        console.log('Layout created:', response.data);
        const newLayoutId = response.data.data.id.toString();
        console.log('New layout ID:', newLayoutId);
        
        // Ensure the new layout is not active by explicitly deactivating it
        try {
          await api.patch(`/parking-layouts/${newLayoutId}/deactivate`);
          console.log('New layout explicitly deactivated');
        } catch (deactivateErr) {
          console.warn('Could not deactivate new layout:', deactivateErr);
        }
        
        setCurrentLayoutId(newLayoutId);
        setIsCurrentPlan(false); // New layouts are NOT active by default - user must explicitly set them
        console.log('Set currentLayoutId to:', newLayoutId, 'and isCurrentPlan to false');
      }

      // Visual layout data is now saved in the settings field above
      
      // Reload layouts to update the list and current status
      await loadAvailableLayouts(false);
      
      setSuccess(currentLayoutId ? 'Plan sauvegardé avec succès!' : 'Nouveau plan créé avec succès!');
      setShowSaveDialog(false);
      setIsCreatingNewPlan(false);
    } catch (err: any) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setError(err.response?.data?.message || err.response?.data?.errors || 'Erreur lors de la sauvegarde');
      setIsCreatingNewPlan(false);
    } finally {
      setSaving(false);
    }
  }, [layout, currentLayoutId, loadAvailableLayouts]);


  // Helper function for deep cloning
  const deepClone = useCallback((obj: any) => {
    return JSON.parse(JSON.stringify(obj));
  }, []);

  // Copy selected element
  const handleCopy = useCallback(() => {
    if (!selectedElement) return;
    
    console.log('Copying element:', selectedElement);
    // Create a deep copy to avoid reference issues
    const elementCopy = deepClone(selectedElement);
    setClipboard(elementCopy);
    setSuccess('Élément copié dans le presse-papier');
  }, [selectedElement, deepClone]);

  // Paste element from clipboard
  const handlePaste = useCallback(async () => {
    if (!clipboard) return;
    
    console.log('Pasting element:', clipboard);
    
    // Create a deep copy of the clipboard element to avoid reference issues
    const clipboardCopy = deepClone(clipboard);
    
    // Create a copy of the element with slight offset
    const offset = 20;
    const timestamp = Date.now();
    
    // Check if it's a parking spot by looking for parking spot properties
    const isParkingSpot = 'assigned_spot_id' in clipboardCopy || 'spot_type' in clipboardCopy || 'is_occupied' in clipboardCopy;
    
    if (isParkingSpot) {
      const existingSpotNumbers = [
        ...availableParkingSpots.map((spot) => spot?.spot_number).filter(Boolean),
        ...layout.spots.map((spot) => spot.spot_number).filter(Boolean),
      ] as string[];

      const sourcePrefix =
        /^([A-Za-z]+)/.exec(clipboardCopy.spot_number || clipboardCopy.text || '')?.[1] || 'P';
      const nextSpotNumber = getNextSpotNumber(existingSpotNumbers, sourcePrefix);

      let createdSpot: any;
      try {
        const response = await api.post('/parking-spots', {
          spot_number: nextSpotNumber,
          location: clipboardCopy.location || 'Plan de parking',
          spot_type: clipboardCopy.spot_type || 'regular',
          is_occupied: false,
        });
        createdSpot = response.data.data || response.data;
        setAvailableParkingSpots((prev) => [...prev, createdSpot]);
      } catch (err) {
        console.error('Error creating copied parking spot:', err);
        setError('Impossible de coller la place: création de la nouvelle place échouée');
        return;
      }

      // It's a parking spot - create completely new object
      setLayout((prev) => {
        const z = computeNextLayerZ(prev);
        const spot: ParkingSpot = {
          ...(clipboardCopy as ParkingSpot),
          id: `visual_spot_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
          assigned_spot_id: createdSpot.id.toString(),
          spot_number: createdSpot.spot_number || nextSpotNumber,
          location: createdSpot.location || 'Plan de parking',
          spot_type: createdSpot.spot_type || 'regular',
          is_occupied: false,
          color: SPOT_TYPES[(createdSpot.spot_type || 'regular') as keyof typeof SPOT_TYPES]?.color || '#2196F3',
          text: createdSpot.spot_number || nextSpotNumber,
          textColor: '#ffffff',
          x: Math.min(clipboardCopy.x + offset, prev.width - clipboardCopy.width),
          y: Math.min(clipboardCopy.y + offset, prev.height - clipboardCopy.height),
          layerZIndex: z,
          layerHidden: false,
          layerLocked: false,
        };
        const next = { ...prev, spots: [...prev.spots, spot] };
        addToHistory(next);
        setSelectedElement(spot);
        return next;
      });
    } else {
      setLayout((prev) => {
        const z = computeNextLayerZ(prev);
        const el: LayoutElement = {
          ...(clipboardCopy as LayoutElement),
          id: `element_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
          name: `${(clipboardCopy as LayoutElement).name || 'Élément'} copie`,
          x: Math.min(clipboardCopy.x + offset, prev.width - clipboardCopy.width),
          y: Math.min(clipboardCopy.y + offset, prev.height - clipboardCopy.height),
          layerZIndex: z,
          layerHidden: false,
          layerLocked: false,
        };
        const next = { ...prev, elements: [...prev.elements, el] };
        addToHistory(next);
        setSelectedElement(el);
        return next;
      });
    }

    setSuccess('Élément collé avec succès');
  }, [clipboard, layout, addToHistory, deepClone, availableParkingSpots]);

  // Update element properties
  const updateElementProperty = useCallback((property: string, value: any) => {
    if (!selectedElement) return;
    
    const updatedLayout = { ...layout };
    
    if (layout.spots.some(spot => spot.id === selectedElement.id)) {
      // Update parking spot
      const spotIndex = updatedLayout.spots.findIndex(s => s.id === selectedElement.id);
      if (spotIndex >= 0) {
        updatedLayout.spots[spotIndex] = { ...updatedLayout.spots[spotIndex], [property]: value };
      }
      } else {
      // Update layout element
      const elementIndex = updatedLayout.elements.findIndex(e => e.id === selectedElement.id);
      if (elementIndex >= 0) {
        updatedLayout.elements[elementIndex] = { ...updatedLayout.elements[elementIndex], [property]: value };
      }
    }
    
    setLayout(updatedLayout);
    addToHistory(updatedLayout);
    
    // Update selected element to reflect changes
    if (layout.spots.some(spot => spot.id === selectedElement.id)) {
      const updatedSpot = updatedLayout.spots.find(s => s.id === selectedElement.id);
      if (updatedSpot) setSelectedElement(updatedSpot);
    } else {
      const updatedElement = updatedLayout.elements.find(e => e.id === selectedElement.id);
      if (updatedElement) setSelectedElement(updatedElement);
    }
  }, [selectedElement, layout, addToHistory]);

  // Update parking spot details in backend
  const updateParkingSpotDetails = useCallback(async (spotId: string, updates: any) => {
    setSpotDetailsLoading(true);
    try {
      await api.put(`/parking-spots/${spotId}`, updates);
      setSuccess('Détails de la place mis à jour');
      // Reload parking spots to get updated data
      await loadAvailableParkingSpots();
    } catch (err) {
      console.error('Error updating parking spot details:', err);
      setError('Erreur lors de la mise à jour des détails');
    } finally {
      setSpotDetailsLoading(false);
    }
  }, [setSuccess, setError]);

  // Delete selected element
  const handleDelete = useCallback(() => {
    if (!selectedElement) return;
    if (isLayerLocked(selectedElement)) return;

    const updatedLayout = { ...layout };
    
    if (layout.spots.some(spot => spot.id === selectedElement.id)) {
      updatedLayout.spots = updatedLayout.spots.filter(s => s.id !== selectedElement.id);
      } else {
      updatedLayout.elements = updatedLayout.elements.filter(e => e.id !== selectedElement.id);
    }

    setLayout(updatedLayout);
    setSelectedElement(null);
    addToHistory(updatedLayout);
  }, [selectedElement, layout, addToHistory]);

  const reorderLayerRowsFromDrag = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      setLayout((prev) => {
        const rows = buildLayerRows(prev);
        if (fromIndex < 0 || fromIndex >= rows.length || toIndex < 0 || toIndex >= rows.length) {
          return prev;
        }
        const nextRows = [...rows];
        const [row] = nextRows.splice(fromIndex, 1);
        nextRows.splice(toIndex, 0, row);
        const total = nextRows.length;
        let elements = [...prev.elements];
        let spots = [...prev.spots];
        nextRows.forEach((r, idx) => {
          const newZ = total - idx;
          if (r.kind === 'element') {
            elements = elements.map((el) => (el.id === r.id ? { ...el, layerZIndex: newZ } : el));
          } else {
            spots = spots.map((sp) => (sp.id === r.id ? { ...sp, layerZIndex: newZ } : sp));
          }
        });
        const next = { ...prev, elements, spots };
        addToHistory(next);
        return next;
      });
    },
    [addToHistory]
  );

  const toggleLayerHidden = useCallback(
    (kind: LayerKind, id: string) => {
      setLayout((prev) => {
        let next: ParkingLayout;
        if (kind === 'element') {
          const current = prev.elements.find((e) => e.id === id);
          const v = !(current?.layerHidden ?? false);
          next = {
            ...prev,
            elements: prev.elements.map((e) => (e.id === id ? { ...e, layerHidden: v } : e)),
          };
        } else {
          const current = prev.spots.find((s) => s.id === id);
          const v = !(current?.layerHidden ?? false);
          next = {
            ...prev,
            spots: prev.spots.map((s) => (s.id === id ? { ...s, layerHidden: v } : s)),
          };
        }
        addToHistory(next);
        return next;
      });
    },
    [addToHistory]
  );

  const toggleLayerLockedRow = useCallback(
    (kind: LayerKind, id: string) => {
      setLayout((prev) => {
        let next: ParkingLayout;
        if (kind === 'element') {
          const current = prev.elements.find((e) => e.id === id);
          const v = !(current?.layerLocked ?? false);
          next = {
            ...prev,
            elements: prev.elements.map((e) => (e.id === id ? { ...e, layerLocked: v } : e)),
          };
        } else {
          const current = prev.spots.find((s) => s.id === id);
          const v = !(current?.layerLocked ?? false);
          next = {
            ...prev,
            spots: prev.spots.map((s) => (s.id === id ? { ...s, layerLocked: v } : s)),
          };
        }
        addToHistory(next);
        return next;
      });
    },
    [addToHistory]
  );

  const selectLayerFromPanel = useCallback(
    (kind: LayerKind, id: string) => {
      if (kind === 'element') {
        const el = layout.elements.find((e) => e.id === id);
        if (el) setSelectedElement(el);
      } else {
        const sp = layout.spots.find((s) => s.id === id);
        if (sp) setSelectedElement(sp);
      }
    },
    [layout.elements, layout.spots]
  );

  const handleBackgroundFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      if (!currentLayoutId) {
        setError('Créez ou sélectionnez un plan enregistré avant d\'ajouter une image d\'arrière-plan.');
        return;
      }
      const formData = new FormData();
      formData.append('background_image', file);
      try {
        const response = await api.post(`/parking-layouts/${currentLayoutId}/background-image`, formData);
        const path = response.data?.data?.background_image ?? response.data?.background_image;
        if (!path) {
          setError('Réponse serveur invalide après envoi de l\'image.');
          return;
        }
        setLayout((prev) => {
          const next: ParkingLayout = {
            ...prev,
            canvas: {
              width: prev.canvas?.width,
              height: prev.canvas?.height,
              background_color: prev.canvas?.background_color ?? '#ffffff',
              background_image_scale: prev.canvas?.background_image_scale,
              background_image: path,
            },
          };
          addToHistory(next);
          return next;
        });
        setSuccess('Image de fond téléversée');
      } catch (err) {
        console.error(err);
        setError('Échec du téléversement de l\'image de fond.');
      }
    },
    [currentLayoutId, addToHistory]
  );

  const clearCanvasBackgroundImage = useCallback(async () => {
    if (!currentLayoutId) {
      setLayout((prev) => {
        const next: ParkingLayout = {
          ...prev,
          canvas: {
            width: prev.canvas?.width,
            height: prev.canvas?.height,
            background_color: prev.canvas?.background_color ?? '#ffffff',
            background_image_scale: prev.canvas?.background_image_scale,
            background_image: undefined,
          },
        };
        addToHistory(next);
        return next;
      });
      return;
    }
    try {
      await api.delete(`/parking-layouts/${currentLayoutId}/background-image`);
      setLayout((prev) => {
        const next: ParkingLayout = {
          ...prev,
          canvas: {
            width: prev.canvas?.width,
            height: prev.canvas?.height,
            background_color: prev.canvas?.background_color ?? '#ffffff',
            background_image_scale: prev.canvas?.background_image_scale,
            background_image: undefined,
          },
        };
        addToHistory(next);
        return next;
      });
      setSuccess('Image de fond retirée');
    } catch (err) {
      console.error(err);
      setError('Impossible de supprimer l\'image de fond.');
    }
  }, [currentLayoutId, addToHistory]);

  const updateBackgroundScale = useCallback(
    (value: number) => {
      const clamped = Math.min(300, Math.max(5, Math.round(value)));
      setLayout((prev) => ({
        ...prev,
        canvas: {
          ...(prev.canvas || {}),
          background_color: prev.canvas?.background_color || '#ffffff',
          background_image_scale: clamped,
        },
        layer_settings: { ...(prev.layer_settings || {}), background_image_scale: clamped },
      }));
    },
    []
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              redo();
      } else {
              undo();
            }
            break;
          case 'c':
            e.preventDefault();
            handleCopy();
            break;
          case 'v':
            e.preventDefault();
            handlePaste();
            break;
          case 's':
            e.preventDefault();
            setShowSaveDialog(true);
            break;
        }
      } else if (e.key === 'Delete' && selectedElement) {
        handleDelete();
      } else if (e.key === 'Escape') {
        setSelectedElement(null);
        setSelectedTool('select');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, handleCopy, handlePaste, selectedElement, handleDelete]);

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      bgcolor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 9999,
    }}>
      {/* Top Toolbar */}
      <Paper sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        p: 1, 
        borderRadius: 0,
        borderBottom: '1px solid #e0e0e0',
        height: 64, // Fixed height
        minHeight: 64,
        maxHeight: 64,
        overflow: 'hidden'
      }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={() => navigate('/parking')} 
            size="large"
            title="Retour au parking"
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">
            Éditeur de Parking
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Layout Selection */}
        <Button
          variant="outlined"
          onClick={() => setShowLayoutSelectionDialog(true)}
          sx={{ minWidth: 180, justifyContent: 'space-between', height: 40 }}
          title="Sélectionner un plan de parking"
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {currentLayoutId 
                  ? availableLayouts.find(l => l.id.toString() === currentLayoutId)?.name || 'Plan sélectionné'
                  : 'Aucun plan sélectionné'
                }
              </Typography>
              {isCurrentPlan && (
                <Chip 
                  label="Actuel" 
                  color="success" 
                  size="small"
                  variant="outlined"
                />
              )}
              </Box>
        </Button>

        <Divider orientation="vertical" flexItem />

        {/* Drawing Tools */}
        <ToggleButtonGroup
          value={selectedTool}
          exclusive
          onChange={(_, value) => value && setSelectedTool(value)}
          size="small"
          sx={{ height: 40 }}
        >
          <ToggleButton value="select" title="Sélectionner (S)">
            <SelectIcon />
          </ToggleButton>
          <ToggleButton value="parking" title="Place de parking (P)">
            <ParkingIcon />
          </ToggleButton>
          <ToggleButton value="rectangle" title="Rectangle (R)">
            <RectangleIcon />
          </ToggleButton>
          <ToggleButton value="circle" title="Cercle (C)">
            <CircleIcon />
          </ToggleButton>
          <ToggleButton value="triangle" title="Triangle (T)">
            <ChangeHistoryIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        {/* View Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton 
            onClick={handleZoomOut} 
            disabled={zoom <= MIN_ZOOM}
            title="Zoom arrière"
          >
            <ZoomOutIcon />
          </IconButton>

          <Chip 
            label={`${Math.round(zoom * 100)}%`} 
            size="small" 
            onClick={handleCenterView}
            sx={{ cursor: 'pointer', minWidth: 60 }}
            title="Niveau de zoom - Cliquer pour centrer"
          />
          
          <IconButton 
            onClick={handleZoomIn} 
            disabled={zoom >= MAX_ZOOM}
            title="Zoom avant"
          >
            <ZoomInIcon />
          </IconButton>

          <IconButton 
            onClick={handleCenterView}
            title="Centrer la vue"
          >
            <CenterIcon />
          </IconButton>

          <IconButton 
            onClick={() => setShowGrid(!showGrid)} 
            color={showGrid ? 'primary' : 'default'}
            title="Afficher/Masquer la grille"
          >
            <GridIcon />
          </IconButton>

          <IconButton 
            onClick={() => setShowPlanSizeDialog(true)}
            title="Paramètres du plan"
          >
            <SettingsIcon />
          </IconButton>

          <IconButton 
            onClick={() => setPanZoomMode(!panZoomMode)} 
            color={panZoomMode ? 'primary' : 'default'}
            title={panZoomMode ? "Désactiver navigation" : "Activer navigation"}
          >
            <OpenWithIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right Section - History & Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton 
            onClick={undo} 
            disabled={historyIndex <= 0}
            title="Annuler (Ctrl+Z)"
          >
            <UndoIcon />
          </IconButton>

          <IconButton 
            onClick={redo} 
            disabled={historyIndex >= history.length - 1}
            title="Refaire (Ctrl+Shift+Z)"
          >
            <RedoIcon />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <IconButton 
            onClick={handleCopy} 
            disabled={!selectedElement}
            title="Copier (Ctrl+C)"
          >
            <CopyIcon />
          </IconButton>

          <IconButton 
            onClick={handlePaste} 
            disabled={!clipboard}
            title={`Coller (Ctrl+V)${clipboard ? ` - ${('assigned_spot_id' in clipboard || 'spot_type' in clipboard || 'is_occupied' in clipboard) ? 'Place' : 'Élément'}` : ''}`}
          >
            <PasteIcon />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <IconButton 
            onClick={handleRotateLeft} 
            disabled={!selectedElement || isLayerLocked(selectedElement)}
            title="Rotation à gauche (15°)"
          >
            <RotateLeftIcon />
          </IconButton>

          <IconButton 
            onClick={handleRotateRight} 
            disabled={!selectedElement || isLayerLocked(selectedElement)}
            title="Rotation à droite (15°)"
          >
            <RotateRightIcon />
          </IconButton>

          <IconButton 
            onClick={handleDelete} 
            disabled={!selectedElement || isLayerLocked(selectedElement)}
            color="error"
            title="Supprimer (Delete)"
          >
            <DeleteIcon />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !currentLayoutId}
            size="small"
            sx={{ height: 40 }}
            title="Sauvegarder le plan"
          >
            Sauvegarder
          </Button>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Canvas Area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden', 
          position: 'relative',
          marginRight: '376px' /* sidebar width */
        }}>
          <Box
          ref={canvasRef}
          sx={{
            width: '100%',
            height: '100%',
            cursor: panZoomMode ? (isPanning ? 'grabbing' : 'grab') : (selectedTool === 'select' ? 'default' : 'crosshair'),
            transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)`,
            transformOrigin: 'top left',
          }}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onWheel={handleWheel}
          onMouseLeave={() => {
            setIsPanning(false);
            setIsDragging(false);
            setIsDrawing(false);
            setIsResizing(false);
          }}
        >
          {/* Grid */}
          {showGrid && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: layout.width,
                height: layout.height,
                backgroundImage: `
                  linear-gradient(to right, #e0e0e0 1px, transparent 1px),
                  linear-gradient(to bottom, #e0e0e0 1px, transparent 1px)
                `,
                backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
                opacity: 0.45,
                pointerEvents: 'none',
                zIndex: 4,
              }}
            />
          )}

          {/* Solid canvas background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: layout.width,
              height: layout.height,
              bgcolor: layout.canvas?.background_color || '#ffffff',
              border: '2px solid #e0e0e0',
              zIndex: 0,
            }}
          />

          {/* Background image (under grid & shapes, above fill) */}
          {resolvedBackgroundUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: layout.width,
                height: layout.height,
                zIndex: 2,
                overflow: 'hidden',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={resolvedBackgroundUrl}
                alt=""
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transformOrigin: 'center center',
                  transform: `scale(${Math.max(0.05, backgroundScalePercent / 100)})`,
                }}
              />
            </Box>
          )}

          {/* No Plan Selected Message */}
          {!currentLayoutId && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 10,
              }}
            >
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Aucun plan sélectionné
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                Cliquez sur "Sélectionner un plan" pour commencer à éditer
              </Typography>
              <Button
                variant="contained"
                onClick={() => setShowLayoutSelectionDialog(true)}
              >
                Sélectionner un plan
              </Button>
            </Box>
          )}

          {/* Shapes & spots — render order controlled by layerZIndex */}
          {sortedRenderable.map(({ kind, item }) => {
            if (item.layerHidden) {
              return null;
            }

            const stackZ = 10 + Math.min(5000, Math.max(0, item.layerZIndex ?? 1));

            if (kind === 'element') {
              const element = item as LayoutElement;
              return (
                <Box
                  key={`el-${element.id}`}
                  sx={{
                    position: 'absolute',
                    left: element.x,
                    top: element.y,
                    width: element.width,
                    height: element.height,
                    zIndex: stackZ,
                    backgroundColor: element.color,
                    border:
                      selectedElement?.id === element.id
                        ? '2px solid #1976d2'
                        : element.borderWidth && element.borderWidth > 0
                          ? `${element.borderWidth}px solid ${element.borderColor || '#000'}`
                          : 'none',
                    borderRadius: element.type === 'circle' ? '50%' : 1,
                    clipPath: element.type === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : undefined,
                    cursor: selectedTool === 'select' ? 'move' : 'crosshair',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 1,
                    transform: `rotate(${element.rotation}deg)`,
                    '&:hover': {
                      opacity: selectedTool === 'select' ? 0.8 : undefined,
                      boxShadow: selectedTool === 'select' ? 2 : undefined,
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedTool === 'select') {
                      const freshElement = layout.elements.find((el) => el.id === element.id);
                      if (freshElement) {
                        setSelectedElement(freshElement);
                      }
                    }
                  }}
                >
                  {element.text && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: element.fontSize || 12,
                        color: element.textColor || '#000000',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        overflow: 'hidden',
                        textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                      }}
                    >
                      {element.text}
                    </Typography>
                  )}
                </Box>
              );
            }

            const spot = item as ParkingSpot;
            return (
              <Box
                key={`sp-${spot.id}`}
                sx={{
                  position: 'absolute',
                  left: spot.x,
                  top: spot.y,
                  width: spot.width,
                  height: spot.height,
                  zIndex: stackZ,
                  backgroundColor: spot.assigned_spot_id ? spot.color : '#e0e0e0',
                  border:
                    selectedElement?.id === spot.id
                      ? '3px solid #1976d2'
                      : spot.borderWidth && spot.borderWidth > 0
                        ? `${spot.borderWidth}px solid ${spot.borderColor || '#000'}`
                        : 'none',
                  borderRadius: 1,
                  cursor: selectedTool === 'select' ? 'move' : 'crosshair',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${spot.rotation}deg)`,
                  '&:hover': {
                    opacity: selectedTool === 'select' ? 0.8 : undefined,
                    boxShadow: selectedTool === 'select' ? 3 : undefined,
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedTool === 'select') {
                    const freshSpot = layout.spots.find((s) => s.id === spot.id);
                    if (freshSpot) {
                      setSelectedElement(freshSpot);
                    }
                  }
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: spot.textColor || (spot.assigned_spot_id ? 'white' : '#666'),
                    fontSize: spot.fontSize || '12px',
                    textAlign: 'center',
                    userSelect: 'none',
                    textShadow: spot.assigned_spot_id ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none',
                  }}
                >
                  {spot.text || (spot.assigned_spot_id ? spot.spot_number || 'N/A' : '')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontSize: '8px',
                    textAlign: 'center',
                    userSelect: 'none',
                  }}
                >
                  {spot.spot_type ? SPOT_TYPES[spot.spot_type as keyof typeof SPOT_TYPES]?.label || 'N/A' : 'N/A'}
                </Typography>
              </Box>
            );
          })}

          {/* Resize Handles for Selected Element */}
          {selectedElement && selectedTool === 'select' && !isLayerLocked(selectedElement) && (
            <>
              {/* Corner handles */}
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x - 4,
                  top: selectedElement.y - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'nw-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('nw', e)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x + selectedElement.width - 4,
                  top: selectedElement.y - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'ne-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('ne', e)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x - 4,
                  top: selectedElement.y + selectedElement.height - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'sw-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('sw', e)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x + selectedElement.width - 4,
                  top: selectedElement.y + selectedElement.height - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'se-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('se', e)}
              />

              {/* Edge handles */}
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x + selectedElement.width / 2 - 4,
                  top: selectedElement.y - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'n-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('n', e)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x + selectedElement.width / 2 - 4,
                  top: selectedElement.y + selectedElement.height - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 's-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('s', e)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x - 4,
                  top: selectedElement.y + selectedElement.height / 2 - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'w-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('w', e)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x + selectedElement.width - 4,
                  top: selectedElement.y + selectedElement.height / 2 - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#1976d2',
                  border: '1px solid white',
                  cursor: 'e-resize',
                  zIndex: 9050,
                }}
                onMouseDown={(e) => handleResizeStart('e', e)}
              />

              {/* Rotation Handle */}
              <Box
                sx={{
                  position: 'absolute',
                  left: selectedElement.x + selectedElement.width / 2 - 4,
                  top: selectedElement.y - 20,
                  width: 8,
                  height: 8,
                  backgroundColor: '#ff9800',
                  border: '1px solid white',
                  cursor: 'grab',
                  zIndex: 9050,
                  borderRadius: '50%',
                  '&:hover': {
                    backgroundColor: '#f57c00',
                    transform: 'scale(1.2)',
                  },
                }}
                onMouseDown={handleRotationStart}
              />
            </>
          )}



          {/* Drawing Preview */}
          {isDrawing && dragStart && currentMousePos && (
            <Box
              sx={{
                position: 'absolute',
                left: Math.min(dragStart.x, currentMousePos.x),
                top: Math.min(dragStart.y, currentMousePos.y),
                width: Math.abs(currentMousePos.x - dragStart.x),
                height: Math.abs(currentMousePos.y - dragStart.y),
                border: '2px dashed #1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                pointerEvents: 'none',
              }}
            />
          )}
          </Box>
        </Box>

        {/* Properties Panel - Always Visible */}
        <Paper
          sx={{
            position: 'fixed',
            top: 64, // Match the header height
            right: 0,
            width: 376,
            height: 'calc(100vh - 64px)', // Account for header height
            borderRadius: '8px 0 0 0',
            borderRight: 'none',
            p: 2,
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
          }}
        >
            <input
              ref={backgroundFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              hidden
              onChange={handleBackgroundFileSelected}
            />

            <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon color="primary" fontSize="small" />
                  <Typography fontWeight={600}>Arrière-plan</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    Le téléversement nécessite un plan déjà créé dans la liste (ID en base).
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    disabled={!currentLayoutId || saving}
                    onClick={() => backgroundFileInputRef.current?.click()}
                  >
                    Choisir une image…
                  </Button>
                  {layout.canvas?.background_image && (
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                      {layout.canvas.background_image}
                    </Typography>
                  )}
                  <Box>
                    <Typography variant="caption" sx={{ mb: 0.5 }}>
                      Échelle ({backgroundScalePercent}%)
                    </Typography>
                    <Slider
                      size="small"
                      value={backgroundScalePercent}
                      min={5}
                      max={300}
                      onChange={(_, v) => updateBackgroundScale(Array.isArray(v) ? v[0] : v)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}%`}
                    />
                  </Box>
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    onClick={clearCanvasBackgroundImage}
                    disabled={!layout.canvas?.background_image}
                  >
                    Retirer l&apos;image
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded disableGutters sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LayersIcon color="primary" fontSize="small" />
                  <Typography fontWeight={600}>Calques ({layerRows.length})</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0.5 }}>
                {layerRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Ajoutez des formes ou des places pour voir les calques.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {layerRows.map((row, idx) => {
                      const isSelectedItem = !!(selectedElement && selectedElement.id === row.id);
                      return (
                        <Paper
                          key={`lr-${row.kind}-${row.id}`}
                          draggable
                          onDragStart={(e) => {
                            setLayerDragIndex(idx);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', `${idx}`);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (layerDragIndex === null) return;
                            reorderLayerRowsFromDrag(layerDragIndex, idx);
                            setLayerDragIndex(null);
                          }}
                          onDragEnd={() => setLayerDragIndex(null)}
                          elevation={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            selectLayerFromPanel(row.kind, row.id);
                          }}
                          sx={{
                            p: 0.75,
                            cursor: 'pointer',
                            bgcolor: isSelectedItem ? 'action.selected' : 'background.default',
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                          }}
                        >
                          <DragIndicatorIcon sx={{ opacity: 0.5, fontSize: 18 }} />
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {row.kind === 'element' ? 'Forme:' : 'Place:'}{' '}
                            {row.label}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label={row.layerHidden ? 'Afficher calque' : 'Masquer calque'}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLayerHidden(row.kind, row.id);
                            }}
                          >
                            {row.layerHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={row.layerLocked ? 'Déverrouiller' : 'Verrouiller'}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLayerLockedRow(row.kind, row.id);
                            }}
                          >
                            {row.layerLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                          </IconButton>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Glisser une ligne pour réordonner — le premier de la liste est au premier plan (devant).
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Propriétés</Typography>
              {selectedElement && (
                <IconButton 
                  size="small"
                  onClick={() => {
                    setSelectedElement(null);
                  }}
                >
                  <BackIcon />
                </IconButton>
              )}
            </Box>

            {selectedElement ? (
              <Stack spacing={2}>
              {/* Element Type */}
              <Typography variant="subtitle2" color="textSecondary">
                {layout.spots.some(spot => spot.id === selectedElement.id) ? 'Place de parking' : (selectedElement as LayoutElement).name}
              </Typography>

              {/* Parking Spot Details */}
              {layout.spots.some(spot => spot.id === selectedElement.id) ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {(selectedElement as ParkingSpot).assigned_spot_id ? 'Place assignée' : 'Place sans liaison'}
                    </Typography>
                    {(selectedElement as ParkingSpot).assigned_spot_id && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setEditingSpotDetails(!editingSpotDetails)}
                        disabled={spotDetailsLoading}
                      >
                        {editingSpotDetails ? 'Annuler' : 'Modifier'}
                      </Button>
                    )}
                  </Box>

                  {(selectedElement as ParkingSpot).assigned_spot_id ? (
                    <Box>
                      {/* Basic Info */}
                      <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 1, mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Informations de base
                        </Typography>
                        {editingSpotDetails ? (
                          <Stack spacing={2}>
                            <TextField
                              label="Numéro de place"
                              value={(selectedElement as ParkingSpot).spot_number || ''}
                              onChange={(e) => updateElementProperty('spot_number', e.target.value)}
                              size="small"
                              fullWidth
                            />
                            <TextField
                              label="Emplacement"
                              value={(selectedElement as ParkingSpot).location || ''}
                              onChange={(e) => updateElementProperty('location', e.target.value)}
                              size="small"
                              fullWidth
                            />
                            <FormControl fullWidth size="small">
                              <InputLabel>Type de place</InputLabel>
                              <Select
                                value={(selectedElement as ParkingSpot).spot_type || 'regular'}
                                onChange={(e) => updateElementProperty('spot_type', e.target.value)}
                                label="Type de place"
                              >
                                <MenuItem value="regular">Standard</MenuItem>
                                <MenuItem value="maintenance">Maintenance</MenuItem>
                                <MenuItem value="washing">Lavage</MenuItem>
                                <MenuItem value="premium">Premium</MenuItem>
                              </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={async () => {
                                  const updates = {
                                    spot_number: (selectedElement as ParkingSpot).spot_number,
                                    location: (selectedElement as ParkingSpot).location,
                                    spot_type: (selectedElement as ParkingSpot).spot_type,
                                  };
                                  await updateParkingSpotDetails((selectedElement as ParkingSpot).assigned_spot_id!, updates);
                                  setEditingSpotDetails(false);
                                }}
                                disabled={spotDetailsLoading}
                              >
                                {spotDetailsLoading ? <CircularProgress size={16} /> : 'Sauvegarder'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setEditingSpotDetails(false)}
                                disabled={spotDetailsLoading}
                              >
                                Annuler
                              </Button>
                            </Box>
                          </Stack>
                        ) : (
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              <strong>Numéro:</strong> {(selectedElement as ParkingSpot).spot_number}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Emplacement:</strong> {(selectedElement as ParkingSpot).location}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Type:</strong> {(selectedElement as ParkingSpot).spot_type}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Statut:</strong> {(selectedElement as ParkingSpot).is_occupied ? 'Occupée' : 'Libre'}
                            </Typography>
                          </Stack>
                        )}
                      </Box>

                      {/* Additional Details */}
                      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Détails supplémentaires
                        </Typography>
                        {editingSpotDetails ? (
                          <Stack spacing={2}>
                            <TextField
                              label="Notes"
                              value={(selectedElement as any).notes || ''}
                              onChange={(e) => updateElementProperty('notes', e.target.value)}
                              size="small"
                              fullWidth
                              multiline
                              rows={3}
                            />
                            <TextField
                              label="Équipements"
                              value={(selectedElement as any).amenities || ''}
                              onChange={(e) => updateElementProperty('amenities', e.target.value)}
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={async () => {
                                  const updates = {
                                    notes: (selectedElement as any).notes,
                                    amenities: (selectedElement as any).amenities,
                                  };
                                  await updateParkingSpotDetails((selectedElement as ParkingSpot).assigned_spot_id!, updates);
                                }}
                                disabled={spotDetailsLoading}
                              >
                                {spotDetailsLoading ? <CircularProgress size={16} /> : 'Sauvegarder'}
                              </Button>
                            </Box>
                          </Stack>
                        ) : (
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              <strong>Notes:</strong> {(selectedElement as any).notes || 'Aucune note'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Équipements:</strong> {(selectedElement as any).amenities || 'Aucun équipement'}
                            </Typography>
                          </Stack>
                        )}
                      </Box>

                      {/* Assignment Actions */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => setShowSpotAssignmentDialog(true)}
                        >
                          Réassigner
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          onClick={() => {
                            updateElementProperty('assigned_spot_id', undefined);
                            updateElementProperty('spot_number', undefined);
                            updateElementProperty('location', undefined);
                            updateElementProperty('spot_type', undefined);
                            updateElementProperty('is_occupied', false);
                            updateElementProperty('color', '#e0e0e0');
                          }}
                        >
                          Désassigner
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        Cette place visuelle n'est pas encore liée à une place de parking réelle.
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setShowSpotAssignmentDialog(true)}
                      >
                        Assigner une place
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <TextField
                  label="Nom"
                  value={(selectedElement as LayoutElement).name || ''}
                  onChange={(e) => updateElementProperty('name', e.target.value)}
                  size="small"
                  fullWidth
                />
              )}

              {/* Text/Label */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Texte</Typography>
                <TextField
                  label="Texte affiché"
                  value={selectedElement.text || ''}
                  onChange={(e) => updateElementProperty('text', e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="Entrez le texte à afficher"
                />
                {selectedElement.text && (
                  <>
                    <TextField
                      label="Taille de police"
                      type="number"
                      value={selectedElement.fontSize || 12}
                      onChange={(e) => updateElementProperty('fontSize', Math.max(8, Math.min(72, parseInt(e.target.value) || 12)))}
                      size="small"
                      fullWidth
                      sx={{ mt: 1 }}
                      inputProps={{ min: 8, max: 72 }}
                    />
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>Couleur du texte</Typography>
                      <input
                        type="color"
                        value={selectedElement.textColor || '#000000'}
                        onChange={(e) => updateElementProperty('textColor', e.target.value)}
                        style={{
                          width: '100%',
                          height: 32,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>

              {/* Color */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Couleur</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {[
                    { color: '#f44336', label: 'Rouge' },
                    { color: '#ff9800', label: 'Orange' },
                    { color: '#4caf50', label: 'Vert' },
                    { color: '#2196f3', label: 'Bleu' },
                    { color: '#9c27b0', label: 'Violet' },
                    { color: '#795548', label: 'Marron' },
                    { color: '#424242', label: 'Gris' },
                    { color: '#000000', label: 'Noir' },
                  ].map(({ color, label }) => (
                    <Box
                      key={color}
                      onClick={() => updateElementProperty('color', color)}
                      sx={{
                        width: 30,
                        height: 30,
                        backgroundColor: color,
                        border: selectedElement.color === color ? '3px solid #1976d2' : '1px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        }
                      }}
                      title={label}
                    />
                  ))}
                </Box>
                <input
                  type="color"
                  value={selectedElement.color}
                  onChange={(e) => updateElementProperty('color', e.target.value)}
                  style={{
                    width: '100%',
                    height: 32,
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
              </Box>

              {/* Position */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Position</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="X"
                    type="number"
                    value={Math.round(selectedElement.x)}
                    onChange={(e) => updateElementProperty('x', parseInt(e.target.value) || 0)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Y"
                    type="number"
                    value={Math.round(selectedElement.y)}
                    onChange={(e) => updateElementProperty('y', parseInt(e.target.value) || 0)}
                    size="small"
                    sx={{ flex: 1 }}
          />
        </Box>
              </Box>

              {/* Size */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>Taille</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Largeur"
                    type="number"
                    value={Math.round(selectedElement.width)}
                    onChange={(e) => updateElementProperty('width', Math.max(10, parseInt(e.target.value) || 10))}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Hauteur"
                    type="number"
                    value={Math.round(selectedElement.height)}
                    onChange={(e) => updateElementProperty('height', Math.max(10, parseInt(e.target.value) || 10))}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              {/* Border Options */}
              <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Bordure</Typography>
                  
                  {/* Border Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={(selectedElement.borderWidth || 0) > 0}
                        onChange={(e) => updateElementProperty('borderWidth', e.target.checked ? 2 : 0)}
                        size="small"
                      />
                    }
                    label="Afficher la bordure"
                    sx={{ mb: 1 }}
                  />

                  {/* Border Width */}
                  {(selectedElement.borderWidth || 0) > 0 && (
                    <TextField
                      label="Épaisseur"
                      type="number"
                      value={selectedElement.borderWidth || 2}
                      onChange={(e) => updateElementProperty('borderWidth', Math.max(1, parseInt(e.target.value) || 1))}
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                  )}

                  {/* Border Color */}
                  {(selectedElement.borderWidth || 0) > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>Couleur de bordure</Typography>
                      <input
                        type="color"
                        value={selectedElement.borderColor || '#000000'}
                        onChange={(e) => updateElementProperty('borderColor', e.target.value)}
                        style={{
                          width: '100%',
                          height: 32,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
          />
        </Box>
                  )}
                </Box>


              {/* Delete Button */}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  handleDelete();
                }}
                fullWidth
              >
                Supprimer
              </Button>
            </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Sélectionnez un élément pour voir ses propriétés
                </Typography>
              </Box>
            )}
          </Paper>
      </Box>

      {/* Floating Action Button for Mobile */}
      <SpeedDial
        ariaLabel="Actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
        direction="up"
      >
        <SpeedDialAction
          key="save"
          icon={<SaveIcon />}
          tooltipTitle="Sauvegarder"
          onClick={() => setShowSaveDialog(true)}
        />
      </SpeedDial>

      {/* Save Dialog */}
      <Dialog 
        open={showSaveDialog} 
        onClose={() => setShowSaveDialog(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: 10001 }}
      >
        <DialogTitle>
          {currentLayoutId ? 'Sauvegarder le plan' : 'Créer un nouveau plan'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nom du plan"
              value={layout.name}
              onChange={(e) => setLayout(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={layout.description}
              onChange={(e) => setLayout(prev => ({ ...prev, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (currentLayoutId ? 'Sauvegarder' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      {error && (
        <Alert 
          severity="error" 
        onClose={() => setError(null)}
          sx={{ 
            position: 'fixed', 
            top: 80, 
            right: 16, 
            zIndex: 10000,
            minWidth: 300
          }}
        >
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert 
          severity="success" 
        onClose={() => setSuccess(null)}
          sx={{ 
            position: 'fixed', 
            top: 80, 
            right: 16, 
            zIndex: 10000,
            minWidth: 300
          }}
        >
          {success}
        </Alert>
      )}

      {/* Parking Spot Assignment Dialog */}
      <Dialog
        open={showSpotAssignmentDialog}
        onClose={() => setShowSpotAssignmentDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 10002 }}
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          Assigner à une place de parking
          <Typography variant="body2" color="textSecondary">
            Sélectionnez une place disponible pour l'assigner à cet élément visuel
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '60vh', overflow: 'auto' }}>
            {availableParkingSpots.length === 0 ? (
              <Alert severity="info">
                Aucune place de parking disponible
              </Alert>
            ) : (() => {
              const unassignedSpots = availableParkingSpots.filter(spot =>
                !layout.spots.some(visualSpot => visualSpot.assigned_spot_id === spot.id)
              );
              
              if (unassignedSpots.length === 0) {
                return (
                  <Alert severity="warning">
                    Toutes les places sont déjà assignées
                  </Alert>
                );
              }
              
              return unassignedSpots.map((spot) => (
                <Box
                  key={spot.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                  onClick={() => {
                    updateElementProperty('assigned_spot_id', spot.id);
                    updateElementProperty('spot_number', spot.spot_number);
                    updateElementProperty('location', spot.location);
                    updateElementProperty('spot_type', spot.spot_type);
                    updateElementProperty('is_occupied', spot.is_occupied);
                    updateElementProperty('color', SPOT_TYPES[spot.spot_type as keyof typeof SPOT_TYPES]?.color || '#4CAF50');
                    setShowSpotAssignmentDialog(false);
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {spot.spot_number}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {spot.location}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ 
                        color: SPOT_TYPES[spot.spot_type as keyof typeof SPOT_TYPES]?.color || '#666',
                        fontWeight: 'bold'
                      }}>
                        {SPOT_TYPES[spot.spot_type as keyof typeof SPOT_TYPES]?.label || spot.spot_type}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {spot.is_occupied ? 'Occupée' : 'Libre'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ));
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSpotAssignmentDialog(false)}>
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Layout Selection Dialog */}
      <Dialog
        open={showLayoutSelectionDialog}
        onClose={() => setShowLayoutSelectionDialog(false)}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 10002 }}
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Gérer les plans de parking</Typography>
              <Typography variant="body2" color="textSecondary">
                Sélectionnez un plan pour l'éditer ou définissez le plan actuel (affiché sur l'écran Parking)
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                console.log('Nouveau Plan clicked, current state:', { currentLayoutId, isCurrentPlan });
                // Initialize a blank layout for new plan
                const newLayout = {
                  name: 'Nouveau Plan',
                  description: '',
                  width: CANVAS_WIDTH,
                  height: CANVAS_HEIGHT,
                  spots: [],
                  elements: [],
                };
                console.log('Setting new layout:', newLayout);
                setLayout(newLayout);
                setCurrentLayoutId(null);
                setIsCurrentPlan(false);
                setIsCreatingNewPlan(true);
                setHistory([newLayout]);
                setHistoryIndex(0);
                setSelectedElement(null);
                setShowLayoutSelectionDialog(false);
                setShowSaveDialog(true);
                console.log('Nouveau Plan setup complete, currentLayoutId should be null');
              }}
            >
              Nouveau Plan
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '60vh', overflow: 'auto' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Sélectionner</strong> : Charge le plan dans l'éditeur pour le modifier<br/>
                <strong>Définir comme actuel</strong> : Définit le plan qui s'affiche sur l'écran Parking principal
              </Typography>
            </Alert>
            {availableLayouts.length === 0 ? (
              <Alert severity="info">
                Aucun plan de parking disponible
              </Alert>
            ) : (
              availableLayouts.map((layout) => (
                <Box
                  key={layout.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {layout.name}
                        </Typography>
                        {layout.is_active && (
                          <Chip 
                            label="Plan actuel" 
                            color="success" 
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {layout.description || 'Aucune description'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Créé le: {new Date(layout.created_at).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleLayoutSelect(layout.id.toString())}
                        disabled={layout.id.toString() === currentLayoutId}
                      >
                        {layout.id.toString() === currentLayoutId ? 'Sélectionné' : 'Sélectionner'}
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color={layout.is_active ? "error" : "success"}
                        onClick={() => handleToggleActive(layout.id.toString())}
                      >
                        {layout.is_active ? 'Désactiver' : 'Activer'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLayoutSelectionDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Size Dialog */}
      <Dialog 
        open={showPlanSizeDialog} 
        onClose={() => setShowPlanSizeDialog(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: 10001 }}
      >
        <DialogTitle>Taille du plan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Largeur (px)"
              type="number"
              value={planSizeForm.width}
              onChange={(e) => {
                setPlanSizeForm(prev => ({ ...prev, width: parseInt(e.target.value) || 0 }));
              }}
              fullWidth
            />
            <TextField
              label="Hauteur (px)"
              type="number"
              value={planSizeForm.height}
              onChange={(e) => {
                setPlanSizeForm(prev => ({ ...prev, height: parseInt(e.target.value) || 0 }));
              }}
              fullWidth
            />
            <Typography variant="caption" color="textSecondary">
              Dimensions minimales: 400x300px, maximales: 4000x3000px
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPlanSizeDialog(false)}>
            Annuler
          </Button>
          <Button onClick={handleConfirmPlanSize} variant="contained">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParkingEditor; 